import { useCallback } from 'react';
import { toast } from 'sonner';
import { PatchBuyer, ReadBuyer } from '@tinct/core-api-models';
import { usePatchBuyer } from '@tinct/core-api';
import { buyerLogoVariantKey } from '../../../utils/buyer-logo-url';

export type UseSaveBuyerLogoOptions = {
  buyer: ReadBuyer | null;
  file: File | null;
  fileDataUrl: string | null;
  variantKey: ReturnType<typeof buyerLogoVariantKey>;
  isReplacing: boolean;
  companyName: string;
  onSaved: (updatedBuyer: ReadBuyer) => void;
};

export type UseSaveBuyerLogoReturn = {
  handleSave: () => void;
  isSaving: boolean;
};

/** PATCHes the validated file onto the buyer's variant slot, with toasts. */
export const useSaveBuyerLogo = ({
  buyer,
  file,
  fileDataUrl,
  variantKey,
  isReplacing,
  companyName,
  onSaved,
}: UseSaveBuyerLogoOptions): UseSaveBuyerLogoReturn => {
  const { mutate: patchBuyer, isPending: isSaving } = usePatchBuyer();

  const handleSave = useCallback(() => {
    if (!buyer?.id || !file || !fileDataUrl) return;
    const data: PatchBuyer = {};
    data[variantKey] = {
      base64: fileDataUrl.split(',')[1],
      originalFilename: file.name,
    };
    patchBuyer(
      { buyerId: buyer.id, data },
      {
        onSuccess: (updatedBuyer) => {
          toast.success(
            `Logo ${isReplacing ? 'replaced' : 'added'} for ${companyName || 'this customer'}`,
            { position: 'bottom-center' },
          );
          onSaved(updatedBuyer);
        },
        onError: () => {
          toast.error("We couldn't save this logo. Please try again.", {
            position: 'bottom-center',
          });
        },
      },
    );
  }, [
    buyer,
    file,
    fileDataUrl,
    variantKey,
    patchBuyer,
    isReplacing,
    companyName,
    onSaved,
  ]);

  return { handleSave, isSaving };
};
